import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Loading } from "@/components/Loading";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useBackTo } from "@/hooks/useBackTo";
import { useForm, type FieldPath } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Eye, EyeOff } from "lucide-react";
import { ApiError } from "@/lib/apiClient";
import { toastApiError } from "@/lib/toast";
import { users, roles as rolesApi, type UserDetail, type CreateUserRequest, type RoleSummary, type UpdateUserRequest, type UserDeletionSummary } from "@/features/users/api/users.api";
import { tickets as ticketsApi } from "@/features/tickets/api/tickets.api";
import { branches as branchesApi, groups as groupsApi, type NamedItem } from "@/features/org/api/org.api";
import { useAuth } from "@/features/auth/auth";
import { USER_ROLE_TIERS, USER_ROLE_TIER_LABELS, USER_ROLE_TIER_DESCRIPTIONS, type UserRoleTier } from "@/features/auth/api/auth.api";
import { FEATURES } from "@/config/featureFlags";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSidebar } from "@/components/ui/sidebar";
import {
  Select as UISelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchableSelect, SEARCHABLE_MIN_OPTIONS } from "@/components/SearchableSelect";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "sonner";
import { TextField, MaskedField, SelectField, DateField, NumberField, useAutoFocus, useUnsavedChangesWarning } from "@/components/form";
import { UserSelect } from "@/features/users/components/UserSelect";
import { DeleteUserCard, useDeletionSummary, type DeletionSummaryState } from "@/features/users/components/DeleteUser";
import { digitsOnly, formatEin, formatPhone, formatRouting, formatSsn, formatZip, isAbaRouting, isEin, isEmail, isPhone10, isRouting9, isSsn9, isZip } from "@/utils/format";
import { OWNERSHIP_TYPES, canonicalOwnershipType, isSoleProprietor } from "@/features/onboarding/components/wizard/constants";
import { STATE_OPTIONS, stateCode } from "@/utils/states";
import { dateOnly } from "@/utils/dates";
import { useZipAutoFill } from "@/hooks/useZipAutoFill";
import { PartnerLinkField } from "@/features/onboarding/components/PartnerLinkField";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AttachmentGrid, AttachmentTile } from "@/components/AttachmentTile";
import { Notice } from "@/components/Notice";
import { saveBlob } from "@/utils/download";
import { useConfirm } from "@/components/ConfirmDialog";
import { useFileViewer } from "@/components/FileViewerDialog";
import { Dash } from "@/components/Dash";
import { FIELD_MAX, tooLong } from "@/lib/fieldLimits";

const NONE = "__none__";
const NONE_OPTIONS = [{ value: NONE, label: "— None —" }];
const PICK_USER_OPTIONS = [{ value: NONE, label: "Pick a user…" }];

const TABS = [
  ["profile", "Profile"],
  ["account", "Account Management"],
  ["residual", "Residual"],
  ["team", "Team"],
] as const;

type TabKey = (typeof TABS)[number][0];

type UserDocument = "license" | "check";

const DOCUMENT_LABELS: Record<UserDocument, string> = {
  license: "Driver's license",
  check: "Voided check",
};

interface DocumentMeta {
  filename: string;
  contentType: string | null;
  sizeBytes: number;
}

interface FormValues {
  email: string;
  username: string;
  name: string;
  password: string;
  tier: UserRoleTier;
  status: "Active" | "Inactive";
  userNumber: number | null;
  assignedRoleId: string;
  managerId: string;
  startDate: string;
  jobTitle: string;
  branch: string;
  group: string;
  dateOfBirth: string;
  phone: string;
  irsReportingName: string;
  firstName: string;
  middleName: string;
  lastName: string;
  ssn: string;
  addressLine1: string;
  city: string;
  state: string;
  zip: string;
  legalName: string;
  ownershipType: string;
  ein: string;
  businessPhone: string;
  businessEmail: string;
  bankRoutingNumber: string;
  bankAccountNumber: string;
  businessAddressLine1: string;
  businessCity: string;
  businessState: string;
  businessZip: string;
}

function blankValues(): FormValues {
  return {
    email: "", username: "", name: "", password: "",
    tier: "USER", status: "Active", userNumber: null,
    assignedRoleId: "", managerId: "", startDate: "",
    jobTitle: "", branch: "", group: "",
    dateOfBirth: "", phone: "",
    irsReportingName: "", firstName: "", middleName: "", lastName: "",
    ssn: "",
    addressLine1: "", city: "", state: "", zip: "",
    legalName: "", ownershipType: "", ein: "",
    businessPhone: "", businessEmail: "",
    bankRoutingNumber: "", bankAccountNumber: "",
    businessAddressLine1: "", businessCity: "", businessState: "", businessZip: "",
  };
}

function clearableValues(v: FormValues) {
  return {
    dateOfBirth: v.dateOfBirth || null,
    phone: v.phone.trim() || null,
    firstName: v.firstName.trim() || null,
    middleName: v.middleName.trim() || null,
    lastName: v.lastName.trim() || null,
    irsReportingName: v.irsReportingName.trim() || null,
    ssn: v.ssn.trim() || null,
    addressLine1: v.addressLine1.trim() || null,
    city: v.city.trim() || null,
    state: v.state || null,
    zip: v.zip.trim() || null,
    legalName: v.legalName.trim() || null,
    ownershipType: v.ownershipType || null,
    ein: v.ein.trim() || null,
    businessPhone: v.businessPhone.trim() || null,
    businessEmail: v.businessEmail.trim() || null,
    bankRoutingNumber: v.bankRoutingNumber.trim() || null,
    bankAccountNumber: v.bankAccountNumber.trim() || null,
    businessAddressLine1: v.businessAddressLine1.trim() || null,
    businessCity: v.businessCity.trim() || null,
    businessState: v.businessState || null,
    businessZip: v.businessZip.trim() || null,
  };
}

export default function UserEditPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const backTo = useBackTo("/manage/users");
  const { user } = useAuth();
  const viewFile = useFileViewer();
  const confirm = useConfirm();
  const isEdit = id !== undefined;

  const [loadedPassword, setLoadedPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [docMeta, setDocMeta] = useState<Partial<Record<UserDocument, DocumentMeta>>>({});
  const [tab, setTab] = useState<TabKey>("profile");
  const [branchOptions, setBranchOptions] = useState<NamedItem[]>([]);
  const [groupOptions, setGroupOptions] = useState<NamedItem[]>([]);

  const [roles, setRoles] = useState<RoleSummary[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const editingSelf = isEdit && user?.userId === id;
  const loadedZip = useRef<string | null>(null);
  const loadedPersonalZip = useRef<string | null>(null);

  const schema = useMemo(
    () =>
      z
        .object({
          email: z.string().trim().min(1, "Email is required.").max(FIELD_MAX.user.email, tooLong("Email", FIELD_MAX.user.email))
            .refine(isEmail, "Enter a valid email address."),
          username: z.string().max(FIELD_MAX.user.username, tooLong("Username", FIELD_MAX.user.username)),
          name: z.string().trim().min(1, "Display name is required.").max(FIELD_MAX.user.displayName, tooLong("Display name", FIELD_MAX.user.displayName)),
          password: z.string(),
          tier: z.enum(["USER", "IT_SUPPORT", "ADMIN"]),
          status: z.enum(["Active", "Inactive"]),
          userNumber: z.number().nullable(),
          assignedRoleId: z.string(),
          managerId: z.string(),
          startDate: z.string(),
          jobTitle: z.string().max(FIELD_MAX.user.jobTitle, tooLong("Job title", FIELD_MAX.user.jobTitle)),
          branch: z.string(),
          group: z.string(),
          dateOfBirth: z.string(),
          phone: z.string(),
          irsReportingName: z.string().max(FIELD_MAX.user.irsReportingName, tooLong("IRS reporting name", FIELD_MAX.user.irsReportingName)),
          firstName: z.string().max(FIELD_MAX.partnerForm.firstName, tooLong("First name", FIELD_MAX.partnerForm.firstName)),
          middleName: z.string().max(FIELD_MAX.partnerForm.middleName, tooLong("Middle name", FIELD_MAX.partnerForm.middleName)),
          lastName: z.string().max(FIELD_MAX.partnerForm.lastName, tooLong("Last name", FIELD_MAX.partnerForm.lastName)),
          ssn: z.string(),
          addressLine1: z.string(),
          city: z.string(),
          state: z.string(),
          zip: z.string(),
          legalName: z.string().max(FIELD_MAX.user.irsReportingName, tooLong("IRS reporting name", FIELD_MAX.user.irsReportingName)),
          ownershipType: z.string().max(FIELD_MAX.user.ownershipType, tooLong("Type of ownership", FIELD_MAX.user.ownershipType)),
          ein: z.string(),
          businessPhone: z.string(),
          businessEmail: z.string().max(FIELD_MAX.user.businessEmail, tooLong("Business email", FIELD_MAX.user.businessEmail)),
          bankRoutingNumber: z.string(),
          bankAccountNumber: z.string(),
          businessAddressLine1: z.string().max(FIELD_MAX.user.addressLine1, tooLong("Business address", FIELD_MAX.user.addressLine1)),
          businessCity: z.string().max(FIELD_MAX.user.city, tooLong("City", FIELD_MAX.user.city)),
          businessState: z.string(),
          businessZip: z.string(),
        })
        .superRefine((v, ctx) => {
          if (!isEdit && v.password.length < 6) {
            ctx.addIssue({ code: "custom", path: ["password"], message: "Password must be at least 6 characters." });
          }
          if (v.userNumber != null && (!Number.isInteger(v.userNumber) || v.userNumber < 1)) {
            ctx.addIssue({ code: "custom", path: ["userNumber"], message: "User # must be a positive integer." });
          }
          if (v.ein.trim() && !isEin(v.ein)) {
            ctx.addIssue({ code: "custom", path: ["ein"], message: "EIN must be 9 digits." });
          }
          if (v.businessPhone.trim() && !isPhone10(v.businessPhone)) {
            ctx.addIssue({ code: "custom", path: ["businessPhone"], message: "Enter a valid 10-digit phone number." });
          }
          if (v.businessEmail.trim() && !isEmail(v.businessEmail)) {
            ctx.addIssue({ code: "custom", path: ["businessEmail"], message: "Enter a valid email address." });
          }
          if (v.bankRoutingNumber.trim()) {
            if (!isRouting9(v.bankRoutingNumber)) {
              ctx.addIssue({ code: "custom", path: ["bankRoutingNumber"], message: "Routing number must be 9 digits." });
            } else if (!isAbaRouting(v.bankRoutingNumber)) {
              ctx.addIssue({ code: "custom", path: ["bankRoutingNumber"], message: "Enter a valid ABA routing number." });
            }
          }
          const account = v.bankAccountNumber.replace(/\D/g, "");
          if (v.bankAccountNumber.trim() && (account.length < 4 || account.length > 17)) {
            ctx.addIssue({ code: "custom", path: ["bankAccountNumber"], message: "Bank account number must be 4 to 17 digits." });
          }
          if (v.phone.trim() && !isPhone10(v.phone)) {
            ctx.addIssue({ code: "custom", path: ["phone"], message: "Enter a valid 10-digit phone number." });
          }
          if (v.zip.trim() && !isZip(v.zip)) {
            ctx.addIssue({ code: "custom", path: ["zip"], message: "Enter a valid ZIP code." });
          }
          if (v.ssn.trim() && !isSsn9(v.ssn)) {
            ctx.addIssue({ code: "custom", path: ["ssn"], message: "SSN must be 9 digits." });
          }
          if (v.businessZip.trim() && !isZip(v.businessZip)) {
            ctx.addIssue({ code: "custom", path: ["businessZip"], message: "Enter a valid ZIP code." });
          }
        }),
    [isEdit],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: blankValues(),
  });

  useAutoFocus(form, "email", !isEdit);

  const watchedPassword = form.watch("password");
  const passwordDirty = isEdit && watchedPassword !== loadedPassword;

  useEffect(() => {
    let cancelled = false;

    rolesApi.list()
      .then((r) => { if (!cancelled) setRoles(r); })
      .catch(() => {  });

    branchesApi.list().then((b) => { if (!cancelled) setBranchOptions(b); }).catch(() => {});
    groupsApi.list().then((g) => { if (!cancelled) setGroupOptions(g); }).catch(() => {});

    if (isEdit && id) {
      users.get(id)
        .then((a) => {
          if (cancelled) return;
          setDetail(a);
          form.reset({
            ...blankValues(),
            email: a.email,
            username: a.username ?? "",
            name: a.name,
            password: "",
            tier: a.role,
            status: a.status === "Inactive" ? "Inactive" : "Active",
            userNumber: a.userNumber ?? null,
            assignedRoleId: a.assignedRoleId ?? "",
            managerId: a.managerId ?? "",
            startDate: dateOnly(a.startDate),
            jobTitle: a.jobTitle ?? "",
            branch: a.branch ?? "",
            group: a.group ?? "",
            dateOfBirth: dateOnly(a.dateOfBirth),
            phone: a.phone ? formatPhone(a.phone) : "",
            firstName: a.firstName ?? "",
            middleName: a.middleName ?? "",
            lastName: a.lastName ?? "",
            irsReportingName: a.irsReportingName ?? "",
            ssn: a.ssn ? formatSsn(a.ssn) : "",
            addressLine1: a.addressLine1 ?? "",
            city: a.city ?? "",
            state: stateCode(a.state) || (a.state ?? ""),
            zip: a.zip ? formatZip(a.zip) : "",
            legalName: a.legalName ?? "",
            ownershipType: canonicalOwnershipType(a.ownershipType ?? ""),
            ein: a.ein ? formatEin(a.ein) : "",
            businessPhone: a.businessPhone ? formatPhone(a.businessPhone) : "",
            businessEmail: a.businessEmail ?? "",
            bankRoutingNumber: a.bankRoutingNumber ?? "",
            bankAccountNumber: a.bankAccountNumber ?? "",
            businessAddressLine1: a.businessAddressLine1 ?? "",
            businessCity: a.businessCity ?? "",
            businessState: stateCode(a.businessState) || (a.businessState ?? ""),
            businessZip: a.businessZip ? formatZip(a.businessZip) : "",
          });
          loadedZip.current = a.businessZip ? formatZip(a.businessZip) : "";
          loadedPersonalZip.current = a.zip ? formatZip(a.zip) : "";
          setLoading(false);
        })
        .catch((e) => {
          if (cancelled) return;
          if (e instanceof ApiError && e.status === 404) setNotFound(true);
          else setError(e instanceof Error ? e.message : "Failed to load user.");
          setLoading(false);
        });
    }

    return () => { cancelled = true; };
  }, [id, isEdit, form]);

  async function onSavePassword() {
    if (!id) return;
    setPasswordError(null);
    setPasswordMsg(null);
    const pw = form.getValues("password");
    if (pw.length < 6) {
      setPasswordError("Password must be at least 6 characters.");
      return;
    }
    setSavingPassword(true);
    try {
      await users.resetPassword(id, pw);
      setLoadedPassword(pw);
      setPasswordMsg("Password saved.");
    } catch (e) {
      setPasswordError(e instanceof ApiError ? e.message : "Couldn't save the password.");
    } finally {
      setSavingPassword(false);
    }
  }

  async function loadDocument(which: UserDocument) {
    if (!id) throw new Error("Missing user.");
    const file = await users.documentFile(id, which);
    setDocMeta((m) => ({
      ...m,
      [which]: {
        filename: file.filename ?? DOCUMENT_LABELS[which],
        contentType: file.blob.type || null,
        sizeBytes: file.blob.size,
      },
    }));
    return file;
  }

  function viewDocument(which: UserDocument) {
    if (!id) return;
    viewFile({
      name: docMeta[which]?.filename ?? DOCUMENT_LABELS[which],
      load: () => loadDocument(which),
      errorMessage: "Couldn't open the document — the file may no longer be available.",
    });
  }

  async function downloadDocument(which: UserDocument) {
    try {
      const file = await loadDocument(which);
      saveBlob(file.blob, file.filename ?? DOCUMENT_LABELS[which]);
    } catch (e) {
      toastApiError(e, "Couldn't download the document.");
    }
  }

  async function onValid(v: FormValues) {
    if (isEdit && !form.formState.isDirty) {
      toast.info("No changes to save");
      return;
    }
    setError(null);
    try {
      if (isEdit && id) {
        const body: UpdateUserRequest = {
          email: v.email,
          name: v.name,
          username: v.username || null,
          role: editingSelf ? undefined : v.tier,
          status: editingSelf ? undefined : v.status,
          userNumber: v.userNumber,
          assignedRoleId: v.assignedRoleId || null,
          managerId: v.managerId || null,
          startDate: v.startDate || null,
          jobTitle: v.jobTitle || null,
          branch: v.branch || null,
          group: v.group || null,
          ...clearableValues(v),
        };

        await users.update(id, body);
      } else {
        const body: CreateUserRequest = {
          email: v.email,
          name: v.name,
          password: v.password,
          username: v.username || null,
          role: v.tier,
          status: v.status,
          userNumber: v.userNumber,
          assignedRoleId: v.assignedRoleId || null,
          managerId: v.managerId || null,
          startDate: v.startDate || null,
          jobTitle: v.jobTitle || null,
          branch: v.branch || null,
          group: v.group || null,
        };
        await users.create(body);
      }
      toast.success(isEdit ? "Changes saved." : "User created.");
      navigate(backTo, { replace: true });
    } catch (e) {
      toastApiError(e, "Could not save changes. Please try again.");
    }
  }

  async function changeTab(next: TabKey) {
    if (next === tab) return;
    if (tab === "profile" && form.formState.isDirty) {
      const leave = await confirm({
        title: "Discard unsaved changes?",
        description: "You have unsaved changes on the Profile tab. Leaving this tab discards them.",
        confirmLabel: "Discard",
        destructive: true,
      });
      if (!leave) return;
      form.reset();
    }
    setTab(next);
  }

  function onInvalid() {
    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>('[aria-invalid="true"]');
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      el?.focus({ preventScroll: true });
    });
  }

  const email = form.watch("email");
  const ownershipType = form.watch("ownershipType");
  const ownershipOptions = useMemo(() => {
    const extra = ownershipType && !OWNERSHIP_TYPES.includes(ownershipType) ? [ownershipType] : [];
    return [...OWNERSHIP_TYPES, ...extra].map((o) => [o, o] as [string, string]);
  }, [ownershipType]);

  useZipAutoFill(form.watch("businessZip"), (state, city) => {
    if (form.getValues("businessZip") === loadedZip.current) return;
    const opts = { shouldValidate: form.formState.isSubmitted, shouldDirty: true };
    if (state) form.setValue("businessState", state, opts);
    if (city) form.setValue("businessCity", city, opts);
  });

  useZipAutoFill(form.watch("zip"), (state, city) => {
    if (form.getValues("zip") === loadedPersonalZip.current) return;
    const opts = { shouldValidate: form.formState.isSubmitted, shouldDirty: true };
    if (state) form.setValue("state", state, opts);
    if (city) form.setValue("city", city, opts);
  });

  const isSoleProp = isSoleProprietor(ownershipType);
  const einOnRecord = (detail?.ein ?? "").trim() !== "";
  const ssnOnRecord = (detail?.ssn ?? "").trim() !== "";

  function taxIdField(kind: "ein" | "ssn", inUse: boolean) {
    const ein = kind === "ein";
    return (
      <MaskedField
        control={form.control}
        name={kind}
        label={ein ? "EIN" : "SSN"}
        placeholder={ein ? "12-3456789" : "123-45-6789"}
        inputMode="numeric"
        format={ein ? formatEin : formatSsn}
        hint={
          inUse
            ? ein
              ? "9 digits — format 12-3456789."
              : "Sole proprietors report under an SSN — 9 digits, format 123-45-6789."
            : `Kept from earlier data — this ownership type reports under ${ein ? "an SSN" : "an EIN"}. Clear it if it no longer applies.`
        }
      />
    );
  }
  const submitting = form.formState.isSubmitting;
  useUnsavedChangesWarning(form.formState.isDirty);

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-4">
        <Link to={backTo} className="text-sm text-muted-foreground hover:underline">
          ← Back to users
        </Link>
      </div>

      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-800">
          {isEdit ? "Edit user" : "New user"}
        </h1>
        {editingSelf && (
          <p className="text-xs text-amber-700 mt-1">
            You're editing your own account — tier, status, and assigned
            role are locked to prevent accidental lockout.
          </p>
        )}
      </header>

      {isEdit && !notFound && !loading && (
        <Tabs value={tab} onValueChange={(v) => changeTab(v as TabKey)} className="mb-5">
          <TabsList variant="underline">
            {TABS.map(([value, label]) => (
              <TabsTrigger key={value} variant="underline" value={value}>
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}

      {notFound ? (
        <Notice tone="info">User not found.</Notice>
      ) : loading ? (
        <Loading>Loading user…</Loading>
      ) : (!isEdit || tab === "profile") ? (
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onValid, onInvalid)}
            className={isEdit ? "space-y-6 pb-20" : "bg-white border border-slate-200 rounded-md shadow-sm p-6"}
          >
            <Section title="Account" plain={!isEdit}>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  <TextField control={form.control} name="email" label="Email" inputMode="email" required autoComplete="off" />
                  <TextField control={form.control} name="username" label="Username (optional)" hint="Login accepts either email or username." autoComplete="off" />
                  <TextField control={form.control} name="name" label="Display name" required autoComplete="off" />

                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Password{!isEdit && <span className="text-destructive"> *</span>}
                        </FormLabel>
                        <div className="relative">
                          <FormControl>
                            <Input
                              type={showPassword ? "text" : "password"}
                              autoComplete="new-password"
                              className="pr-10"
                              {...field}
                            />
                          </FormControl>
                          <button
                            type="button"
                            tabIndex={-1}
                            onClick={() => setShowPassword((s) => !s)}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        {isEdit && (
                          <FormDescription>Leave blank to keep the current password.</FormDescription>
                        )}
                        {passwordDirty && (
                          <div className="mt-2 flex items-center gap-3">
                            <Button type="button" size="sm" onClick={onSavePassword} disabled={savingPassword}>
                              {savingPassword ? "Saving…" : "Save password"}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="link"
                              className="h-auto p-0"
                              onClick={() => { form.setValue("password", loadedPassword); setPasswordError(null); setPasswordMsg(null); }}
                            >
                              Cancel
                            </Button>
                          </div>
                        )}
                        {passwordMsg && <p className="mt-1 text-xs text-emerald-700">{passwordMsg}</p>}
                        {passwordError && <p className="mt-1 text-xs text-destructive">{passwordError}</p>}
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <NumberField control={form.control} name="userNumber" label="User #" min={1} step={1} />
                  <SelectField
                    control={form.control}
                    name="status"
                    label="Status"
                    disabled={editingSelf}
                    options={[["Active", "Active"], ["Inactive", "Inactive (cannot sign in)"]]}
                  />

                  <SelectField
                    control={form.control}
                    name="tier"
                    label="Tier"
                    disabled={editingSelf}
                    options={USER_ROLE_TIERS.map((t) => [
                      t,
                      USER_ROLE_TIER_LABELS[t],
                      USER_ROLE_TIER_DESCRIPTIONS[t],
                    ])}
                  />

                  <NoneSelect
                    control={form.control}
                    name="assignedRoleId"
                    label="Assigned role"
                    hint="Fine-grained permission grants."
                    disabled={editingSelf}
                    placeholder="— None —"
                    options={roles.map((r) => [r.id, `${r.name} (${r.permissionCount} permissions)`])}
                  />

                  <FormField
                    control={form.control}
                    name="managerId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Manager</FormLabel>
                        <FormControl>
                          <UserSelect
                            ref={field.ref}
                            value={field.value || NONE}
                            onValueChange={(v) => field.onChange(v === NONE ? "" : v)}
                            pinnedOptions={NONE_OPTIONS}
                            exclude={id ? [id] : undefined}
                          />
                        </FormControl>
                        <FormDescription>Their manager can view their merchants.</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <NoneSelect
                    control={form.control}
                    name="branch"
                    label="Branch"
                    placeholder="— None —"
                    options={branchOptions.map((o) => [o.name, o.name])}
                  />
                  <NoneSelect
                    control={form.control}
                    name="group"
                    label="Group"
                    placeholder="— None —"
                    options={groupOptions.map((o) => [o.name, o.name])}
                  />
                  <TextField control={form.control} name="jobTitle" label="Job title" autoComplete="off" />

                  <DateField control={form.control} name="startDate" label="Start date" />
                </div>
            </Section>

            {isEdit && id && (
            <Section title="Legal">
              <div className="space-y-4">
                <PartnerLinkField userId={id} userName={detail?.name} />

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  <TextField control={form.control} name="firstName" label="First name" autoComplete="off" />
                  <TextField control={form.control} name="middleName" label="Middle name" autoComplete="off" />
                  <TextField control={form.control} name="lastName" label="Last name" autoComplete="off" />

                  <DateField control={form.control} name="dateOfBirth" label="Date of birth" />
                  <TextField
                    control={form.control}
                    name="phone"
                    label="Phone"
                    placeholder="(720) 555-0185"
                    inputMode="tel"
                    format={formatPhone}
                    autoComplete="off"
                  />
                  <TextField
                    control={form.control}
                    name="addressLine1"
                    label="Address"
                    placeholder="1250 Main Street, Apt 4B"
                    autoComplete="off"
                    className="md:col-span-2 xl:col-span-3"
                  />

                  <TextField
                    control={form.control}
                    name="zip"
                    label="ZIP Code"
                    placeholder="80202"
                    inputMode="numeric"
                    maxLength={10}
                    format={formatZip}
                    autoComplete="off"
                  />
                  <NoneSelect
                    control={form.control}
                    name="state"
                    label="State"
                    placeholder="— Select —"
                    options={STATE_OPTIONS}
                  />
                  <TextField control={form.control} name="city" label="City" autoComplete="off" />
                </div>
              </div>
            </Section>
            )}

            {isEdit && (
            <Section title="IRS/1099">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  <TextField
                    control={form.control}
                    name="irsReportingName"
                    label="IRS Reporting Name"
                    placeholder="Legal name as filed with the IRS"
                    autoComplete="off"
                    className="md:col-span-2"
                  />
                  <NoneSelect
                    control={form.control}
                    name="ownershipType"
                    label="Type of Ownership"
                    placeholder="— Select —"
                    searchable={false}
                    options={ownershipOptions}
                  />

                  {isSoleProp ? (
                    <>
                      {taxIdField("ssn", true)}
                      {einOnRecord && taxIdField("ein", false)}
                    </>
                  ) : (
                    <>
                      {taxIdField("ein", true)}
                      {ssnOnRecord && taxIdField("ssn", false)}
                    </>
                  )}
                  <TextField
                    control={form.control}
                    name="legalName"
                    label="Legal Business Name"
                    placeholder="Acme Coffee Co."
                    autoComplete="off"
                  />
                  <TextField
                    control={form.control}
                    name="businessPhone"
                    label="Business Phone"
                    placeholder="(720) 555-0185"
                    inputMode="tel"
                    format={formatPhone}
                    autoComplete="off"
                  />

                  <TextField
                    control={form.control}
                    name="businessEmail"
                    label="Business Email"
                    inputMode="email"
                    placeholder="billing@business.com"
                    autoComplete="off"
                  />
                  <TextField
                    control={form.control}
                    name="bankRoutingNumber"
                    label="Routing Number"
                    placeholder="9 digits"
                    inputMode="numeric"
                    format={formatRouting}
                    hint="9-digit ABA routing number."
                    autoComplete="off"
                  />
                  <MaskedField
                    control={form.control}
                    name="bankAccountNumber"
                    label="Bank Account Number"
                    placeholder="4–17 digits"
                    format={(v: string) => digitsOnly(v, 17)}
                  />

                  <TextField
                    control={form.control}
                    name="businessAddressLine1"
                    label="Business Address"
                    placeholder="1250 Main Street, Suite 200"
                    autoComplete="off"
                    className="md:col-span-2 xl:col-span-3"
                  />

                  <TextField
                    control={form.control}
                    name="businessZip"
                    label="ZIP Code"
                    placeholder="80202"
                    inputMode="numeric"
                    maxLength={10}
                    format={formatZip}
                    autoComplete="off"
                  />
                  <NoneSelect
                    control={form.control}
                    name="businessState"
                    label="State"
                    placeholder="— Select —"
                    options={STATE_OPTIONS}
                  />
                  <TextField control={form.control} name="businessCity" label="City" autoComplete="off" />
                </div>

              {detail && (detail.hasLicense || detail.hasVoidCheck) && id && (
                <div className="mt-6">
                  <p className="text-sm font-medium text-slate-800 mb-3">
                    Documents ({[detail.hasLicense, detail.hasVoidCheck].filter(Boolean).length})
                  </p>
                  <AttachmentGrid>
                    {(["license", "check"] as const)
                      .filter((w) => (w === "license" ? detail.hasLicense : detail.hasVoidCheck))
                      .map((w) => (
                        <AttachmentTile
                          key={w}
                          filename={docMeta[w]?.filename ?? (w === "license" ? detail.driverLicenseName : detail.voidCheckName) ?? DOCUMENT_LABELS[w]}
                          contentType={docMeta[w]?.contentType}
                          sizeBytes={docMeta[w]?.sizeBytes}
                          onView={() => viewDocument(w)}
                          onDownload={() => downloadDocument(w)}
                        />
                      ))}
                  </AttachmentGrid>
                </div>
              )}
            </Section>
            )}

            {error && <Notice tone="error" className={isEdit ? undefined : "mt-4"}>{error}</Notice>}

            {isEdit ? (
              <StickyFormBar>
                <FormActions submitting={submitting} isEdit={isEdit} />
              </StickyFormBar>
            ) : (
              <div className="mt-6 flex items-center justify-end gap-3">
                <FormActions submitting={submitting} isEdit={isEdit} />
              </div>
            )}
          </form>
        </Form>
      ) : null}

      {isEdit && tab === "account" && user != null && !notFound && !loading && id && (
        editingSelf ? (
          <Notice tone="info">
            Account actions aren't available on your own account — ask another
            admin to sign in as, reassign tickets for, or delete it.
          </Notice>
        ) : (
          <AccountActions userId={id} userEmail={email} userName={detail?.name ?? email} />
        )
      )}

      {isEdit && tab === "team" && !notFound && !loading && (
        <TeamPanel detail={detail} />
      )}

      {isEdit && tab === "residual" && !notFound && !loading && (
        <ResidualPanel detail={detail} />
      )}
    </div>
  );
}

function Section({ title, plain, children }: { title: string; plain?: boolean; children: ReactNode }) {
  if (plain) return <>{children}</>;
  return (
    <Card>
      <CardHeader className="border-b py-3">
        <CardTitle className="text-base font-semibold text-slate-900">{title}</CardTitle>
      </CardHeader>
      <CardContent className="pt-6">{children}</CardContent>
    </Card>
  );
}

function StickyFormBar({ children }: { children: ReactNode }) {
  const { state, isMobile } = useSidebar();
  return (
    <div
      className="fixed bottom-0 right-0 z-20 border-t border-slate-200 bg-white px-4 sm:px-6 transition-[left] duration-200 ease-linear"
      style={{
        left: isMobile ? 0 : state === "collapsed" ? "var(--sidebar-width-icon)" : "var(--sidebar-width)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      <div className="mx-auto flex max-w-4xl items-center justify-end gap-3 py-3">{children}</div>
    </div>
  );
}

function FormActions({ submitting, isEdit }: { submitting: boolean; isEdit: boolean }) {
  const backTo = useBackTo("/manage/users");
  return (
    <>
      <Button asChild variant="ghost">
        <Link to={backTo}>Cancel</Link>
      </Button>
      <Button type="submit" disabled={submitting}>
        {submitting ? "Saving…" : isEdit ? "Save changes" : "Create user"}
      </Button>
    </>
  );
}

function NoneSelect({
  control, name, label, hint, placeholder, options, disabled, searchable,
}: {
  control: ReturnType<typeof useForm<FormValues>>["control"];
  name: FieldPath<FormValues>;
  label: string;
  hint?: string;
  placeholder: string;
  options: [string, string, string?][];
  disabled?: boolean;
  searchable?: boolean;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const selected = typeof field.value === "string" && field.value ? field.value : NONE;
        return (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          {(searchable ?? options.length >= SEARCHABLE_MIN_OPTIONS) ? (
            <FormControl>
              <SearchableSelect
                ref={field.ref}
                value={selected}
                onValueChange={(v) => field.onChange(v === NONE ? "" : v)}
                options={[
                  { value: NONE, label: placeholder },
                  ...options.map(([v, l, sub]) => ({ value: v, label: l, hint: sub })),
                ]}
                searchPlaceholder={`Search ${label.toLowerCase()}…`}
                disabled={disabled}
              />
            </FormControl>
          ) : (
            <UISelect
              value={selected}
              onValueChange={(v) => field.onChange(v === NONE ? "" : v)}
              disabled={disabled}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value={NONE}>{placeholder}</SelectItem>
                {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </UISelect>
          )}
          {hint && <FormDescription>{hint}</FormDescription>}
          <FormMessage />
        </FormItem>
        );
      }}
    />
  );
}

function TeamPanel({ detail }: { detail: UserDetail | null }) {
  if (!detail) return <Loading>Loading…</Loading>;
  return (
    <div className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <h2 className="text-base font-semibold text-slate-900 mb-3">Manager</h2>
        {detail.managerId ? (
          <Link to={`/manage/users/${detail.managerId}`} className="text-slate-900 hover:underline font-medium">
            {detail.managerName ?? "View manager"}
          </Link>
        ) : (
          <p className="text-sm text-slate-500">No manager assigned.</p>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <h2 className="text-base font-semibold text-slate-900 mb-3">
          Sub-agents ({detail.subAgents.length})
        </h2>
        {detail.subAgents.length === 0 ? (
          <p className="text-sm text-slate-500">No direct reports.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {detail.subAgents.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="text-slate-500 tabular-nums">{s.userNumber ?? <Dash />}</TableCell>
                  <TableCell>
                    <Link to={`/manage/users/${s.id}`} className="text-slate-900 hover:underline">{s.name}</Link>
                  </TableCell>
                  <TableCell className="text-slate-700">{s.email ?? <Dash />}</TableCell>
                  <TableCell className="text-slate-700">{s.status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function ResidualPanel({ detail }: { detail: UserDetail | null }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6">
      <h2 className="text-base font-semibold text-slate-900 mb-1">Residual</h2>
      <p className="text-sm text-slate-500">
        Per-agent residual reporting isn't wired up yet. Once the residual
        ledger is in place, this tab will show {detail?.name ?? "this user"}'s
        monthly residual earnings (owner + override splits) and their book of
        merchants.
      </p>
      {FEATURES.salesModule && (
        <Link to="/reports/residuals" className="inline-block mt-3 text-sm text-brand-600 hover:underline">
          Open the residuals report →
        </Link>
      )}
    </div>
  );
}

function SignInAsCard({ userId, userEmail }: { userId: string; userEmail: string }) {
  const { impersonate } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function go() {
    setBusy(true);
    setErr(null);
    try {
      await impersonate(userId);
      navigate("/");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't sign in as this user.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6">
      <h2 className="text-base font-semibold text-slate-900 mb-1">Sign in as this user</h2>
      <p className="text-sm text-slate-500 mb-3">
        View the portal exactly as {userEmail} sees it. A banner will let you return
        to your own account at any time.
      </p>
      <Button type="button" variant="outline" onClick={go} disabled={busy}>
        {busy ? "Switching…" : "Sign in as this user"}
      </Button>
      {err && <p className="text-sm text-destructive mt-2">{err}</p>}
    </div>
  );
}

function AccountActions({ userId, userEmail, userName }: { userId: string; userEmail: string; userName: string }) {
  const deletion = useDeletionSummary(userId);
  return (
    <div className="space-y-6">
      <SignInAsCard userId={userId} userEmail={userEmail} />
      <BulkReassignTickets
        fromUserId={userId}
        fromUserName={userName}
        tickets={deletion.summary?.tickets ?? null}
        onReassigned={deletion.reload}
      />
      <DangerZone userId={userId} userEmail={userEmail} deletion={deletion} />
    </div>
  );
}

function BulkReassignTickets({
  fromUserId,
  fromUserName,
  tickets,
  onReassigned,
}: {
  fromUserId: string;
  fromUserName: string;
  tickets: UserDeletionSummary["tickets"] | null;
  onReassigned: () => void;
}) {
  const [toUserId, setToUserId] = useState("");
  const [includeClosed, setIncludeClosed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);
  const confirm = useConfirm();
  const inScope = tickets ? (includeClosed ? tickets.assignedTotal : tickets.assignedOpen) : null;

  async function submit() {
    if (!toUserId) {
      setFeedback({ kind: "err", msg: "Pick a user to reassign tickets to." });
      return;
    }
    const target = await users.get(toUserId).catch(() => null);
    const what = inScope != null
      ? `${inScope.toLocaleString()} ${includeClosed ? "" : "open "}ticket${inScope === 1 ? "" : "s"}`
      : `every ${includeClosed ? "" : "open "}ticket`;
    const confirmMsg = `Reassign ${what} from ${fromUserName} to ${target?.name ?? "the chosen user"}?`;
    if (!(await confirm({ title: "Reassign tickets?", description: confirmMsg, confirmLabel: "Reassign" }))) return;

    setBusy(true);
    setFeedback(null);
    try {
      const res = await ticketsApi.bulkReassign({ fromUserId, toUserId, includeClosed });
      setFeedback({
        kind: res.success ? "ok" : "err",
        msg: res.message || (res.success ? "Tickets reassigned." : "Reassignment failed."),
      });
      if (res.success) onReassigned();
    } catch (e) {
      setFeedback({
        kind: "err",
        msg: e instanceof ApiError ? e.message : "Reassignment failed.",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-6">
      <h2 className="text-base font-semibold text-slate-900 mb-1">Reassign tickets</h2>
      <p className="text-sm text-slate-500 mb-3">
        Move every ticket currently assigned to <strong>{fromUserName}</strong> to a
        different user. The new assignee gets a notification email per ticket (if SMTP
        is configured).
      </p>
      {tickets && (
        <p className="text-xs text-slate-500 mb-3">
          Currently assigned: {tickets.assignedOpen.toLocaleString()} open ticket{tickets.assignedOpen === 1 ? "" : "s"}
          {" · "}
          {tickets.assignedClosed.toLocaleString()} closed ticket{tickets.assignedClosed === 1 ? "" : "s"}
        </p>
      )}
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex flex-col text-xs text-slate-600 flex-1">
          <span className="mb-1">New assignee</span>
          <UserSelect
            value={toUserId || NONE}
            onValueChange={(v) => setToUserId(v === NONE ? "" : v)}
            pinnedOptions={PICK_USER_OPTIONS}
            exclude={[fromUserId]}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600 sm:pb-2">
          <Checkbox checked={includeClosed} onCheckedChange={(c) => setIncludeClosed(c === true)} />
          Include closed / resolved
        </label>
        <Button type="button" variant="outline" disabled={busy || !toUserId || inScope === 0} onClick={submit} className="whitespace-nowrap">
          {busy ? "Reassigning…" : "Reassign tickets"}
        </Button>
      </div>
      {feedback && (
        <p className={"text-sm mt-3 " + (feedback.kind === "ok" ? "text-emerald-700" : "text-destructive")}>
          {feedback.msg}
        </p>
      )}
    </section>
  );
}

function DangerZone({ userId, userEmail, deletion }: { userId: string; userEmail: string; deletion: DeletionSummaryState }) {
  const [sending, setSending] = useState(false);
  const [sendMsg, setSendMsg] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  async function onSendReset() {
    setSendError(null);
    setSendMsg(null);
    setSending(true);
    try {
      const r = await users.sendResetEmail(userId);
      setSendMsg(`Reset link emailed to ${r.to}. The link expires in 2 hours.`);
    } catch (e) {
      setSendError(e instanceof ApiError ? e.message : "Couldn't send the email.");
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <h3 className="text-base font-semibold text-slate-900 mb-1">Send password reset email</h3>
        <p className="text-sm text-slate-500 mb-3">
          Emails {userEmail} a secure link to set their own new password (valid
          for 2 hours). Their current password keeps working until they complete
          the reset. Requires SMTP to be configured.
        </p>
        <Button type="button" variant="outline" onClick={onSendReset} disabled={sending}>
          {sending ? "Sending…" : "Send reset email"}
        </Button>
        {sendMsg && <p className="mt-2 text-sm text-emerald-700">{sendMsg}</p>}
        {sendError && <p className="mt-2 text-sm text-destructive">{sendError}</p>}
      </div>

      <DeleteUserCard userId={userId} deletion={deletion} />
    </section>
  );
}
